<template>
  <div class="checklist-templates">
    <toolbar
      backRoute="/dashboard"
      title="Checklist templates"
      saveButtonLabel="Add new items"
      saveButtonEvent="show-checklist-items-popup" />

    <d-row>
      <d-col cols="12" md="4" lg="4">
        <table class="templates-list table table-striped table-sm table-bordered table-hover mt-5">
          <thead>
            <tr>
              <th class="d-flex">
                <span>Checklist templates</span>

                <d-button
                  theme="success"
                  class="ml-auto"
                  size="sm"
                  @click.prevent="addTemplate"
                  title="Add template">
                  Add template
                </d-button>
              </th>
            </tr>
          </thead>

          <tbody v-if="!loadError">
            <tr v-if="!allDataLoaded">
              <td class="text-center">
                Loading templates&hellip;
              </td>
            </tr>
            <tr v-if="allDataLoaded && templates.length === 0">
              <td class="text-center">
                There are no templates to display&hellip;
              </td>
            </tr>
            <tr
              v-for="(template, index) of templates"
              :key="'template-' + index">
              <td
                class="align-middle d-flex"
                @click="selectTemplate(template.id)">
                <span>{{ template.name }}</span>

                <d-button
                  theme="primary"
                  class="mr-2 ml-auto"
                  size="sm"
                  title="Change name"
                  @click.prevent.stop="editTemplate(template.name, template.id)">
                  <i class="material-icons">edit</i>
                </d-button>

                <d-button
                  theme="danger"
                  size="sm"
                  :disabled="removeInProgress"
                  @click.prevent.stop="removeTemplate(template.id)"
                  title="Remove template">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>
          </tbody>
        </table>
      </d-col>

      <d-col cols="12" md="8" lg="8">
        <fieldset
          v-if="selectedTemplate !== null"
          class="checklist-template">
          <legend>{{ getSelectedTemplateName() }}</legend>

          <d-row class="d-flex mb-3">
            <d-button
              theme="success"
              class="ml-auto mr-3"
              size="sm"
              title="Add item"
              @click.prevent="addTemplateItem">
              Add checklist item
            </d-button>
          </d-row>

          <d-alert
            v-if="templateItems.length === 0"
            show
            theme="light"
            class="text-center">
            There are no items in this template&hellip;
          </d-alert>

          <table
            v-if="templateItems.length > 0"
            :class="{ 'table table-striped table-sm table-hover': true, 'ordering-save': orderSaveInProgress }">
            <draggable
              v-model="templateItems"
              tag="tbody">
              <tr
                v-for="(item, index) of templateItems"
                :key="'template-item-' + index">
                <td class="text-center align-middle" style="width: 50px;">
                  <i class="material-icons dnd-icon">dehaze</i>
                </td>

                <td class="align-middle">
                  <span>{{ item.name }}</span>
                </td>

                <td class="align-middle" style="width: 100px;">
                  <d-button
                    theme="primary"
                    size="sm"
                    :disabled="removeInProgress"
                    @click.prevent="editTemplateItem(item.id)"
                    title="Edit item"
                    class="ml-auto mr-2">
                    <i class="material-icons">edit</i>
                  </d-button>

                  <d-button
                    theme="danger"
                    size="sm"
                    :disabled="removeInProgress"
                    @click.prevent="removeTemplateItem(item.id)"
                    title="Remove item">
                    <i class="material-icons">delete</i>
                  </d-button>
                </td>
              </tr>
            </draggable>
          </table>
        </fieldset>

        <d-alert
          v-if="selectedTemplate === null"
          show
          theme="light"
          class="mt-5">
          Select a template from the left list&hellip;
        </d-alert>
      </d-col>
    </d-row>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      An error occurred during data loading. <a href="javascript:window.location.reload();" class="alert-link">Reload the page</a> to try again.
    </d-alert>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import vueDraggable from 'vuedraggable';

export default {
  name: 'checklist-templates',
  components: {
    'draggable': vueDraggable
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    }
  },
  data () {
    return {
      loaded: false,
      loadError: false,
      templates: null,
      templateItems: [],
      selectedTemplate: null,
      removeInProgress: false,
      orderSaveInProgress: false
    };
  },
  watch: {
    selectedTemplate (newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      if (newValue !== null) {
        this.loadTemplate(newValue);
      }
    },
    'templateItems': function (newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        return;
      }

      if (this.templateItems.length > 0) {
        this.debouncedSaveOfOrdering();
      }
    }
  },
  mounted () {
    this.loadData();
    this.$bus.$on('checklist-template-list-reload', this.loadData);
    this.$bus.$on('checklist-template-items-list-reload', this.loadTemplate);
  },
  methods: {
    loadData () {
      ListUtils.loadItemsData(
        this,
        '/check-list-templates/items',
        'templates',
        {
          pagination: 0
        }
      );

      this.$bus.$emit('checklist-templates-reload');
    },
    selectTemplate (selectedID) {
      this.selectedTemplate = selectedID;
    },
    addTemplate () {
      this.$bus.$emit('show-checklist-template-popup', 'add');
    },
    editTemplate (name, id) {
      this.$bus.$emit('show-checklist-template-popup', 'edit', name, id);
    },
    loadTemplate (templateID = null) {
      if (templateID === null) {
        templateID = this.selectedTemplate;
      }

      ListUtils.loadItemsData(
        this,
        '/check-list-templates/items/items/' + parseInt(templateID, 10),
        'templateItems',
        {
          pagination: 0
        }
      );
    },
    getSelectedTemplateName () {
      return this.templates.filter(template => template.id === this.selectedTemplate)[0].name;
    },
    removeTemplate (templateID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/check-list-templates/delete/',
        id: templateID,
        successTitle: 'Checklist template removed',
        successText: 'The selected checklist template has been removed',
        confirmTitle: 'Do you really want to remove this checklist template?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('checklist-template-list-reload');

          if (templateID === this.selectedTemplate) {
            this.selectedTemplate = null;
          }
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    addTemplateItem () {
      this.$bus.$emit('show-checklist-item-popup', 'add', 'CHECKLIST', this.selectedTemplate);
    },
    editTemplateItem (itemID) {
      this.$bus.$emit('show-checklist-item-popup', 'edit', 'CHECKLIST', this.selectedTemplate, itemID);
    },
    removeTemplateItem (itemID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/check-list-templates/items/delete/',
        id: parseInt(itemID, 10),
        successTitle: 'Item removed',
        successText: 'The selected item has been removed',
        confirmTitle: 'Do you really want to remove this item?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('checklist-template-items-list-reload');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
        errorAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    // eslint-disable-next-line
    debouncedSaveOfOrdering: debounce(function () {
      this.saveItemsOrder();
    }, 1000),
    saveItemsOrder () {
      this.orderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/check-list-templates/set-ordering/' + parseInt(this.selectedTemplate, 10),
        formData: {
          items: this.templateItems.map(item => item.id)
        },
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during ordering change. Please try again.',
        successAction: () => {
          this.orderSaveInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('checklist-template-list-reload', this.loadData);
    this.$bus.$off('checklist-template-items-list-reload', this.loadTemplate);
  }
};
</script>

<style lang="scss" scoped>
.templates-list td,
.templates-list th,
.templates-list td > span {
  align-items: center;
  display: flex;
  line-height: 30px;
}

.templates-list td {
  cursor: pointer;
}

.checklist-template {
  margin-top: 34px;
}

.dnd-icon {
  color: #999;
  cursor: move;
  font-size: 24px!important;
  position: relative;
  top: 4px!important;
}

.table.ordering-save {
  opacity: .5;
  pointer-events: none;
}
</style>
